import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import IconButton from '@mui/material/IconButton';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { toast } from 'react-toastify';
import useManageOrder from '../order/useManageOrder';
import { useSelector } from 'react-redux';
import { formatDate } from '../../utils/dateUtils';
import { formatAmount } from '../../utils/formatAmount';
import ConfirmImageModal from './ConfirmImageModal';
import OrdererConfirmModal from '../../sharedComponents/OrdererConfirmModal';
import OrderByOptionService from '../setting/orderByOption/OrderByOptionService';
import DispatchJobsSection from '../mercari/mercariAdminDashboard.js/DispatchJobsSection';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Backdrop, CircularProgress, Typography, Portal } from '@mui/material';

const OrdersWithoutConfirmImage = () => {
  const [rowData, setRowData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [postcodeWarning, setPostcodeWarning] = useState(null); // For showing postcode mismatch after save
  const [pendingOrdererMatch, setPendingOrdererMatch] = useState(null);
  const [orderByOptions, setOrderByOptions] = useState([]);
  const [isCheckingOrderer, setIsCheckingOrderer] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false); // bot jobs still running, with "ส่งการ์ดให้…"
  const { 
    orders, 
    isLoading, 
    error, 
    getOrdersWithoutPurchaseConfirm,
    uploadPurchaseConfirmImage 
  } = useManageOrder();
  const shopName = useSelector((state) => state.shop.name);

  useEffect(() => {
    loadOrders();
  }, []);

  // Needed so the modal can list orderers when an order has no LINE match
  // (requireLineGroupLink hold) and matchedOrderers is empty.
  useEffect(() => {
    OrderByOptionService.getActiveOrderByOptions()
      .then((res) => setOrderByOptions(res.data || []))
      .catch(() => setOrderByOptions([]));
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    const formattedRecords = orders.map(record => ({
      ...record,
      orderDate: formatDate(record.orderDate),
      contact: record.CustomerProfile?.contact || '',
      smileName: record.smileNameInput || record.CustomerProfile?.smileName || '',
      amount: formatAmount(record.amount),
    }));
    setRowData(formattedRecords);
  }, [orders]);

  const loadOrders = async () => {
    await getOrdersWithoutPurchaseConfirm();
  };

  // Icon component for adding confirmation image
  const IconComponent = (params) => {
    return (
      <IconButton 
        onClick={() => handleAddImage(params.data)}
        title="Add confirmation image"
      >
        <AddPhotoAlternateIcon style={{ color: 'blue' }} />
      </IconButton>
    );
  };

  // Link renderer for URLs
  const LinkRenderer = (params) => {
    return (
      <div>
        <a 
          target="_blank" 
          href={params.value} 
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {params.value}
        </a>
      </div>
    );
  };

  // Image renderer for product images
  const ImageRenderer = (params) => {
    return (
      <div>
        {params.value ? (
          <img 
            src={params.value} 
            alt="Product" 
            style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }} 
          />
        ) : (
          <span className="text-gray-400">No image</span>
        )}
      </div>
    );
  };

  const handleAddImage = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleImageConfirm = async (imageFile, validationResult = null) => {
    if (!selectedOrder || !imageFile) {
      toast.error('No order or image selected');
      return;
    }

    // Store validation result BEFORE upload (validation is already completed at this point)
    // This ensures we have the result even if upload completes quickly
    const storedValidationResult = validationResult;

    try {
      setIsCheckingOrderer(true);
      const result = await uploadPurchaseConfirmImage(selectedOrder.id, imageFile);
      setIsCheckingOrderer(false);

      if (result && result.success) {
        toast.success(result.message || 'Confirmation image uploaded successfully!');
        if (result.ordererMatch) {
          setPendingOrdererMatch(result.ordererMatch);
        }
        handleModalClose();
        await loadOrders(); // Refresh the list to remove the uploaded order
        
        // Show postcode warning AFTER successful upload (only for mismatch)
        // Use stored validation result to avoid any timing issues
        if (storedValidationResult && !storedValidationResult.isValid && !storedValidationResult.cannotValidate) {
          // Small delay to ensure UI is ready after modal close
          setTimeout(() => {
            setPostcodeWarning(storedValidationResult);
          }, 100);
        }
      } else {
        toast.error('Failed to upload confirmation image');
      }
    } catch (error) {
      toast.error('Error uploading confirmation image');
      console.error('Upload error:', error);
    } finally {
      setIsCheckingOrderer(false);
    }
  };

  const handleImageSkip = async () => {
    // Since hideSkipOption is true, this shouldn't be called
    // But keeping it for safety
    toast.info('Skipped adding confirmation image');
    handleModalClose();
  };

  // Type badge renderer
  const TypeRenderer = (params) => {
    const isHandCarry = params.data.isHandCarry;
    return (
      <span className={`px-2 py-1 rounded text-xs font-bold ${
        isHandCarry 
          ? 'bg-blue-100 text-blue-800' 
          : 'bg-green-100 text-green-800'
      }`}>
        {isHandCarry ? '✈️ Air' : '🚢 Ship'}
      </span>
    );
  };

  // Website badge renderer
  const WebsiteRenderer = (params) => {
    const website = params.value;
    const isMercari = website?.toLowerCase() === 'mercari';
    return (
      <span className={`px-2 py-1 rounded text-xs ${
        isMercari 
          ? 'bg-red-100 text-red-800 font-bold' 
          : 'bg-gray-100 text-gray-700'
      }`}>
        {website || '-'}
      </span>
    );
  };

  const columnDefs = [
    { 
      headerName: 'Type', 
      field: 'isHandCarry', 
      flex: 0.5,
      cellRenderer: TypeRenderer 
    },
    { 
      headerName: 'Website', 
      field: 'website', 
      flex: 0.6,
      cellRenderer: WebsiteRenderer 
    },
    { 
      headerName: 'Contact', 
      field: 'contact', 
      flex: 1,
      cellStyle: { cursor: 'pointer' }
    },
    { 
      headerName: 'Smile Name', 
      field: 'smileName', 
      flex: 1 
    },
    { 
      headerName: 'Order Date', 
      field: 'orderDate', 
      flex: 1 
    },
    { 
      headerName: 'Product Link', 
      field: 'url', 
      flex: 2,
      cellRenderer: LinkRenderer 
    },
    { 
      headerName: 'Product Image', 
      field: 'imageUrl', 
      flex: 1,
      cellRenderer: ImageRenderer 
    },
    { 
      headerName: 'Amount', 
      field: 'amount', 
      flex: 0.8 
    },
    {
      headerName: 'Add Image',
      field: 'actions',
      cellRenderer: IconComponent,
      flex: 0.6,
      sortable: false,
      filter: false
    }
  ];

  return (
    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Orders Without Purchase Confirmation
        </h2>
        <p className="text-gray-600">
          Found {rowData.length} orders without purchase confirmation images
        </p>
        <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setDispatchOpen(true)}>
          ออเดอร์ที่ส่งให้บอทกด (ยังไม่จบ) / ส่งการ์ดใหม่
        </Button>
      </div>

      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        domLayout='autoHeight'
        enableCellTextSelection={true}
        defaultColDef={{ resizable: true }}
        pagination={true}
        paginationPageSize={50}
        getRowHeight={(params) => {
          if (params.data.imageUrl) {
            return 80; // Height for rows with images
          }
          return 50; // Default height
        }}
      />

      {/* Reuse the existing ConfirmImageModal */}
      <ConfirmImageModal 
        isOpen={isModalOpen}
        onRequestClose={handleModalClose}
        onConfirm={handleImageConfirm}
        onSkip={handleImageSkip}
        hideSkipOption={true}
        isHandCarry={selectedOrder?.isHandCarry || false}
        website={selectedOrder?.website || ''}
      />

      {/* Postcode Warning Modal - Shows AFTER successful upload */}
      <Dialog 
        open={postcodeWarning !== null} 
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
          <div className="flex items-center gap-2">
            <span className="text-3xl">🚨</span>
            <span className="font-bold">ตรวจพบรหัสไปรษณีย์ไม่ตรงกับโกดัง!</span>
          </div>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#fef2f2' }}>
          <div className="mt-4 p-4 bg-white rounded-lg border-2 border-red-400">
            <pre className="text-red-800 whitespace-pre-wrap text-base font-bold">
              {postcodeWarning?.message}
            </pre>
          </div>
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-300">
            <p className="text-yellow-800 font-medium">
              ⚠️ รูปถูกอัพโหลดแล้ว แต่อาจส่งผิดโกดัง กรุณาตรวจสอบและดำเนินการแก้ไข
            </p>
          </div>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#fef2f2', justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => setPostcodeWarning(null)}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 200 }}
          >
            รับทราบ (Acknowledge)
          </Button>
        </DialogActions>
      </Dialog>
      <DispatchJobsSection open={dispatchOpen} onClose={() => setDispatchOpen(false)} defaultStatus="live" />
      <OrdererConfirmModal
        ordererMatch={pendingOrdererMatch}
        orderByOptions={orderByOptions}
        onConfirmed={() => setPendingOrdererMatch(null)}
      />
      <Portal>
        <Backdrop open={isCheckingOrderer} sx={{ color: '#fff', zIndex: 99999, flexDirection: 'column', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>กำลังบันทึก / ตรวจสอบ orderer...</Typography>
        </Backdrop>
      </Portal>
    </div>
  );
};

export default OrdersWithoutConfirmImage;